import { useEffect, useRef } from 'react';

/**
 * Custom hook to handle persistence of application state to localStorage.
 * 
 * @param {Object} config - Configuration object
 * @param {string} config.key - The localStorage key to use
 * @param {Object} config.state - The current state object to save
 * @param {Function} config.onLoad - Callback function to restore state
 * @param {boolean} config.canLoad - Boolean indicating if it's safe to load (e.g., data is fetched)
 */
const usePersistence = ({ key, state, onLoad, canLoad }) => {
    const isLoaded = useRef(false);

    // Load data on mount (or when canLoad becomes true)
    useEffect(() => {
        if (canLoad && !isLoaded.current) {
            try {
                const savedData = localStorage.getItem(key);
                if (savedData) {
                    const parsedData = JSON.parse(savedData);
                    onLoad(parsedData);
                }
            } catch (error) {
                console.error("Failed to load persistence data:", error);
            } finally {
                isLoaded.current = true;
            }
        }
    }, [canLoad, key, onLoad]);

    // Save data on state change
    useEffect(() => {
        if (isLoaded.current) {
            try {
                localStorage.setItem(key, JSON.stringify(state));
            } catch (error) {
                console.error("Failed to save persistence data:", error);
            }
        }
    }, [state, key]);

    const resetConfig = () => {
        try {
            localStorage.removeItem(key);
            window.location.reload();
        } catch (error) {
            console.error("Failed to reset config:", error);
        }
    };

    return { resetConfig };
};

export default usePersistence;
// Revisado: 2025-12-16
